import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  Text,
  Spinner,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Radio,
  RadioGroup,
  Stack,
  Textarea,
  Button,
  Divider,
  Alert,
  AlertIcon,
  useToast,
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import Navbar from "../components/Navbar";
import StarRating from "../components/StarRating";
import { apiUrl } from "../config";
import { useLanguage } from "../contexts/LanguageContext";

const PRIOR_VISIT_OPTIONS = [
  "within_week",
  "within_month",
  "within_3_months",
  "within_year",
  "over_year",
  "never",
];

const RETURN_LIKELIHOOD_OPTIONS = [
  "very_likely",
  "somewhat_likely",
  "somewhat_unlikely",
  "very_unlikely",
];

const PostRedemptionSurvey = () => {
  const { token } = useParams();
  const { language, setLanguage, translate } = useLanguage();
  const toast = useToast();

  const [loadState, setLoadState] = useState("loading"); // loading | ready | used | expired | error
  const [context, setContext] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchToken = async () => {
      try {
        const res = await axios.get(`${apiUrl}/survey/${token}`);
        if (cancelled) return;
        if (res.data.status === "ready") {
          setContext(res.data);
          if (res.data.customerLanguage) {
            setLanguage(res.data.customerLanguage);
          }
          setLoadState("ready");
        } else if (res.data.status === "used") {
          setLoadState("used");
        } else {
          setLoadState("expired");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading survey", err);
        setLoadState("error");
      }
    };
    fetchToken();
    return () => {
      cancelled = true;
    };
  }, [token, setLanguage]);

  const initialValues = {
    priorVisit: "",
    serviceRating: 0,
    returnLikelihood: "",
    businessFeedback: "",
    coupalRating: 0,
    coupalSuggestions: "",
  };

  const validationSchema = Yup.object().shape({
    priorVisit: Yup.string()
      .oneOf(PRIOR_VISIT_OPTIONS)
      .required(translate("survey.errors.required")),
    serviceRating: Yup.number()
      .min(1, translate("survey.errors.rating"))
      .max(5)
      .required(translate("survey.errors.rating")),
    returnLikelihood: Yup.string()
      .oneOf(RETURN_LIKELIHOOD_OPTIONS)
      .required(translate("survey.errors.required")),
    businessFeedback: Yup.string().max(
      1000,
      translate("survey.errors.tooLong"),
    ),
    coupalRating: Yup.number()
      .min(1, translate("survey.errors.rating"))
      .max(5)
      .required(translate("survey.errors.rating")),
    coupalSuggestions: Yup.string().max(
      1000,
      translate("survey.errors.tooLong"),
    ),
  });

  const handleSubmit = async (values, actions) => {
    try {
      await axios.post(`${apiUrl}/survey/${token}`, values);
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 409) {
        setLoadState("used");
      } else if (err.response?.status === 410) {
        setLoadState("expired");
      } else {
        toast({
          title: translate("survey.submitError.title"),
          description: translate("survey.submitError.description"),
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  const renderShell = (children) => (
    <Box
      minH="100vh"
      bg="primaryBackground"
      display="flex"
      flexDirection="column"
    >
      <Navbar external={true} />
      <Container maxW="640px" py={{ base: 8, md: 12 }} flex="1">
        {children}
      </Container>
    </Box>
  );

  if (loadState === "loading") {
    return renderShell(
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="secondary" />
      </Box>,
    );
  }

  if (loadState === "error") {
    return renderShell(
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {translate("survey.loadError")}
      </Alert>,
    );
  }

  if (loadState === "used") {
    return renderShell(
      <Box textAlign="center" py={10}>
        <Heading size="md" mb={4}>
          {translate("survey.alreadySubmitted.title")}
        </Heading>
        <Text color="gray.600">
          {translate("survey.alreadySubmitted.body")}
        </Text>
      </Box>,
    );
  }

  if (loadState === "expired") {
    return renderShell(
      <Box textAlign="center" py={10}>
        <Heading size="md" mb={4}>
          {translate("survey.expired.title")}
        </Heading>
        <Text color="gray.600">{translate("survey.expired.body")}</Text>
      </Box>,
    );
  }

  if (submitted) {
    return renderShell(
      <Box textAlign="center" py={10}>
        <Heading size="md" mb={4}>
          {translate("survey.thankYou.title")}
        </Heading>
        <Text color="gray.600">{translate("survey.thankYou.body")}</Text>
      </Box>,
    );
  }

  const businessName = context?.businessName || "";
  const customerName = context?.customerName || "";

  const interpolate = (key, vars) => {
    let str = translate(key);
    Object.entries(vars || {}).forEach(([k, v]) => {
      str = str.replace(new RegExp(`\\{${k}\\}`, "g"), v);
    });
    return str;
  };

  return renderShell(
    <Box>
      <Heading size="lg" mb={2}>
        {translate("survey.heading")}
      </Heading>
      <Text color="gray.600" mb={8}>
        {interpolate("survey.intro", { customerName })}
      </Text>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form>
            <VStack spacing={8} align="stretch">
              <Field name="priorVisit">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.priorVisit && form.touched.priorVisit
                    }
                    isRequired
                  >
                    <FormLabel>
                      {interpolate("survey.priorVisit.question", {
                        businessName,
                      })}
                    </FormLabel>
                    <RadioGroup
                      value={field.value}
                      onChange={(v) => form.setFieldValue("priorVisit", v)}
                    >
                      <Stack direction="column" spacing={2}>
                        {PRIOR_VISIT_OPTIONS.map((opt) => (
                          <Radio key={opt} value={opt}>
                            {translate(`survey.priorVisit.options.${opt}`)}
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                    <FormErrorMessage>
                      {form.errors.priorVisit}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field name="serviceRating">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.serviceRating && form.touched.serviceRating
                    }
                    isRequired
                  >
                    <FormLabel>
                      {interpolate("survey.serviceRating.question", {
                        businessName,
                      })}
                    </FormLabel>
                    <StarRating
                      value={field.value}
                      onChange={(v) => {
                        form.setFieldValue("serviceRating", v);
                        form.setFieldTouched("serviceRating", true, false);
                      }}
                      ariaLabel={translate("survey.serviceRating.question")}
                    />
                    <FormErrorMessage>
                      {form.errors.serviceRating}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field name="returnLikelihood">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.returnLikelihood &&
                      form.touched.returnLikelihood
                    }
                    isRequired
                  >
                    <FormLabel>
                      {translate("survey.returnLikelihood.question")}
                    </FormLabel>
                    <RadioGroup
                      value={field.value}
                      onChange={(v) =>
                        form.setFieldValue("returnLikelihood", v)
                      }
                    >
                      <Stack direction="column" spacing={2}>
                        {RETURN_LIKELIHOOD_OPTIONS.map((opt) => (
                          <Radio key={opt} value={opt}>
                            {translate(
                              `survey.returnLikelihood.options.${opt}`,
                            )}
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                    <FormErrorMessage>
                      {form.errors.returnLikelihood}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field name="businessFeedback">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.businessFeedback &&
                      form.touched.businessFeedback
                    }
                  >
                    <FormLabel>
                      {interpolate("survey.businessFeedback.question", {
                        businessName,
                      })}
                    </FormLabel>
                    <Textarea
                      {...field}
                      rows={4}
                      maxLength={1000}
                      placeholder={translate(
                        "survey.businessFeedback.placeholder",
                      )}
                    />
                    <FormErrorMessage>
                      {form.errors.businessFeedback}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Divider />

              <Field name="coupalRating">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.coupalRating && form.touched.coupalRating
                    }
                    isRequired
                  >
                    <FormLabel>
                      {translate("survey.coupalRating.question")}
                    </FormLabel>
                    <StarRating
                      value={field.value}
                      onChange={(v) => {
                        form.setFieldValue("coupalRating", v);
                        form.setFieldTouched("coupalRating", true, false);
                      }}
                      ariaLabel={translate("survey.coupalRating.question")}
                    />
                    <FormErrorMessage>
                      {form.errors.coupalRating}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field name="coupalSuggestions">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.coupalSuggestions &&
                      form.touched.coupalSuggestions
                    }
                  >
                    <FormLabel>
                      {translate("survey.coupalSuggestions.question")}
                    </FormLabel>
                    <Textarea
                      {...field}
                      rows={4}
                      maxLength={1000}
                      placeholder={translate(
                        "survey.coupalSuggestions.placeholder",
                      )}
                    />
                    <FormErrorMessage>
                      {form.errors.coupalSuggestions}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Button
                type="submit"
                variant="secondary"
                size="lg"
                isLoading={formik.isSubmitting}
                width="100%"
              >
                {translate("survey.submit")}
              </Button>
            </VStack>
          </Form>
        )}
      </Formik>
    </Box>,
  );
};

export default PostRedemptionSurvey;
